import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import * as yup from 'yup';
import {
  SparkForm,
  SparkFormButton,
  SparkSlider,
} from 'sharedComponents/SparkForm';
import { Star03 } from 'components/SVG';
import { parseJSONCleaned } from 'utilities/index';
import { numericSort } from 'utilities/sort';
import { useResize } from 'hooks/common';
import type { PriceOverCredits } from '.';
import type { SubscriptionCreditOptions } from 'types/entityTypes/SubscriptionCreditOptions';
import type { SubscriptionBillingInterval } from 'types/entityTypes/SubscriptionBillingInterval';
import type {
  Feature,
  SubscriptionType,
} from 'types/entityTypes/SubscriptionType';

type GroupedBySubscriptionBillingIntervalID = {
  [intervalID: string]: SubscriptionCreditOptions[];
};

type SubscriptionCardProps = {
  type: SubscriptionType;
  subscriptionTypesLength: number | undefined;
  subscriptionTypeID: string | undefined;
  subscriptionBillingInterval: SubscriptionBillingInterval | undefined;
  currentSelectedCreditOption: SubscriptionCreditOptions[] | undefined;
  priceOverCredits: PriceOverCredits[] | undefined;
  groupedBySubscriptionBillingIntervalID:
    | GroupedBySubscriptionBillingIntervalID
    | undefined;
  onSliceChange: (credits: number, typeID: string, priceID: string) => void;
  getSubscription: (
    subscriptionCreditOptionID: string,
    priceID: string
  ) => void;
  isAddingCheckoutSession: boolean;
  isFirstCard?: boolean;
  subscriptionCreditOptionsID: string | undefined;
};

const SubscriptionCard = ({
  type,
  subscriptionTypesLength,
  subscriptionTypeID,
  subscriptionBillingInterval,
  currentSelectedCreditOption,
  priceOverCredits,
  onSliceChange,
  groupedBySubscriptionBillingIntervalID,
  getSubscription,
  isAddingCheckoutSession: isLoading,
  isFirstCard = false,
  subscriptionCreditOptionsID,
}: SubscriptionCardProps) => {
  const { isMobile } = useResize();
  const [disableGetProButton, setDisableGetProButton] = React.useState(false);

  const freeCreditOptions =
    groupedBySubscriptionBillingIntervalID?.[type.attributes.name];

  const currentSubTypeData = currentSelectedCreditOption?.filter(
    (options) => options.attributes.subscriptionTypeID === type.id
  );
  const creditOptions = freeCreditOptions || currentSubTypeData;

  const currentSubTypeCredits = creditOptions
    ?.map((curr) => ({
      credits: curr.attributes.credits,
      price: curr.attributes.price,
      priceID: curr.attributes.stripePriceID,
      creditOptionID: curr.id,
    }))
    .sort((a, b) => numericSort(a.credits, b.credits));

  const creditsSliderValues = currentSubTypeCredits?.map(
    (value) => value.credits
  );

  const priceOverCredit = priceOverCredits?.find((item) => item.id === type.id);

  const creditIndex = creditsSliderValues?.findIndex(
    (value) => value === priceOverCredit?.credits
  );
  const selectedCreditOption = currentSubTypeCredits?.find(
    (credit) => subscriptionCreditOptionsID === credit.creditOptionID
  );
  const currentPrice =
    creditIndex !== undefined && creditIndex !== -1
      ? currentSubTypeCredits?.[creditIndex]
      : (selectedCreditOption ?? currentSubTypeCredits?.[0]);

  const features = parseJSONCleaned<Feature[]>(type.attributes.features);
  React.useEffect(() => {
    if (
      (type.id === subscriptionTypeID &&
        currentPrice?.creditOptionID === subscriptionCreditOptionsID) ||
      isLoading
    ) {
      setDisableGetProButton(true);
      return;
    }
    setDisableGetProButton(false);
  }, [
    currentPrice?.creditOptionID,
    isLoading,
    subscriptionCreditOptionsID,
    subscriptionTypeID,
    type.id,
  ]);

  const handleSubmit = () => {
    getSubscription(
      currentPrice?.creditOptionID || '',
      currentPrice?.priceID || ''
    );
  };

  const calculatedCreditsIndex =
    creditsSliderValues?.indexOf(selectedCreditOption?.credits ?? 0) ?? -1;

  const initialValue = {
    credits: calculatedCreditsIndex === -1 ? 0 : calculatedCreditsIndex,
  };

  const formatPrice = (price: number) => {
    return price % 1 === 0 ? String(price) : price.toFixed(2);
  };

  return (
    <Box
      key={type.id}
      sx={{
        border: '1px solid black',
        borderRadius: type.attributes.mostPopular ? 0 : '15px',
        width: isMobile ? '100%' : `${93 / (subscriptionTypesLength || 4)}%`,
        position: 'relative',
        ...(type.attributes.mostPopular && {
          borderBottomLeftRadius: '15px',
          borderBottomRightRadius: '15px',
          ...(isMobile && { marginTop: '47px' }),
        }),
      }}
    >
      {type.attributes.mostPopular && (
        <Box
          padding={1.5}
          sx={{
            background: 'black',
            position: 'absolute',
            right: '-1px',
            left: '-1px',
            borderTopLeftRadius: '15px',
            borderTopRightRadius: '15px',
            top: isMobile ? '-48px' : '-55px',
          }}
        >
          <Typography
            sx={{
              color: 'white',
              fontFamily: 'Mont-semibold',
              textAlign: 'center',
            }}
            variant='h5'
          >
            Most Popular
          </Typography>
        </Box>
      )}
      <Box paddingY={2} paddingX={3.5}>
        <Box className='content' display='flex' gap={2} flexDirection='column'>
          <Box display='flex' gap={1} flexDirection='column'>
            <Typography variant='h5' fontFamily='Mont-semibold'>
              {type.attributes.name}
              {type.id === subscriptionTypeID && (
                <Chip
                  sx={{
                    borderRadius: '5px',
                    marginLeft: 1,
                    bgcolor: (theme) => theme.palette.primary.light,
                    '& span': {
                      fontFamily: 'Mont-SemiBold',
                      fontSize: '14px',
                    },
                  }}
                  label={'Current'}
                />
              )}
            </Typography>
            <Typography variant='body2'>
              {type.attributes.description}
            </Typography>
          </Box>
          <Box display='flex' alignItems='center' gap={1}>
            <Typography variant='h3' fontFamily='Mont-semibold'>
              $
              {!!freeCreditOptions
                ? formatPrice(
                    freeCreditOptions[0]?.attributes.price /
                      (subscriptionBillingInterval?.attributes.name === 'Year'
                        ? 12
                        : 1)
                  )
                : formatPrice(
                    (currentPrice?.price || 0) /
                      (subscriptionBillingInterval?.attributes.name === 'Year'
                        ? 12
                        : 1)
                  )}
            </Typography>

            <Typography sx={{ color: '#888' }}>
              per{' '}
              {subscriptionBillingInterval?.attributes.name === 'Year'
                ? 'Month'
                : subscriptionBillingInterval?.attributes.name}
            </Typography>
          </Box>

          <Box>
            <Typography textTransform='lowercase'>
              {isFirstCard
                ? '500 credits once off'
                : !!freeCreditOptions
                  ? freeCreditOptions?.[0]?.attributes.credits
                  : currentPrice?.credits}{' '}
              {isFirstCard
                ? ''
                : `credits/${
                    subscriptionBillingInterval?.attributes.name === 'Year'
                      ? 'month'
                      : subscriptionBillingInterval?.attributes.name
                  }`}
            </Typography>
            <SparkForm
              initialValues={initialValue}
              onSave={handleSubmit}
              validationSchema={yup.object({})}
            >
              {!isFirstCard ? (
                <SparkSlider
                  name='credits'
                  label=''
                  min={0}
                  max={
                    creditsSliderValues?.length
                      ? creditsSliderValues.length - 1
                      : 0
                  }
                  valueLabelDisplay='off'
                  step={1}
                  onChange={(credit) => {
                    const creditValue = creditsSliderValues?.[credit as number];
                    onSliceChange(
                      creditValue || 0,
                      type.id,
                      currentPrice?.priceID || ''
                    );
                  }}
                  renderValue={false}
                />
              ) : (
                <Box margin={4} />
              )}
              <SparkFormButton
                variant='contained'
                sx={{
                  borderRadius: '20px',
                  width: '98%',
                  padding: '10px 10px',
                }}
                disabled={disableGetProButton}
              >
                {type.attributes.buttonText}
              </SparkFormButton>
            </SparkForm>
          </Box>
          <Box display='flex' gap={2} flexDirection='column' paddingTop={1}>
            {features?.map((feature, index) => (
              <Box key={index} display='flex' alignItems='center' gap={2}>
                {feature.iconURL ? (
                  <img
                    src={feature.iconURL}
                    alt={feature.label}
                    width={20}
                    height={20}
                  />
                ) : (
                  <Star03 fill='black' />
                )}
                <>{feature.label}</>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SubscriptionCard;
